// ============================================================
// ExposurePresetPicker - the exposure picker's preset badges
// ============================================================
// Painted, Shrouded, Breached, the Fear ladder… each a badge coloured by
// whether it helps the attacker. Pressing one opens its duration row;
// a duration adds it under the shared fields and closes the row.

import { useState } from 'react';
import { EXPOSURE_PRESETS, presetCode } from '../engine/exposures';
import { onActivateKey } from '../utils/keyActivation';
import { exposureColour } from './exposureColour';
import styles from '../styles/AddConditionModal.module.css';

const DURATION_PRESETS = [1, 2, 3, 5, 10] as const;

/** `onPick(id, rounds)`: rounds 0 = until removed. */
export function ExposurePresetPicker({ onPick }: { onPick: (id: string, rounds: number) => void }) {
  // Which preset is showing its duration row.
  const [durationFor, setDurationFor] = useState<string | null>(null);
  const [customDuration, setCustomDuration] = useState('');
  const toggle = (id: string) => {
    setDurationFor(durationFor === id ? null : id);
    setCustomDuration('');
  };
  const pick = (id: string, rounds: number) => {
    onPick(id, rounds);
    setDurationFor(null);
    setCustomDuration('');
  };
  return (
    <div className={styles.categorySection}>
      <div className={styles.categoryHeader}>Presets</div>
      <div className={styles.badgeGrid}>
        {EXPOSURE_PRESETS.map((p) => {
          const cc = exposureColour(presetCode(p.id));
          return (
            <span
              key={p.id}
              className={styles.condBadge}
              style={{ background: cc.bg, borderColor: cc.border, color: cc.text }}
              role="button"
              tabIndex={0}
              title={p.hint}
              onClick={() => toggle(p.id)}
              onKeyDown={onActivateKey(() => toggle(p.id))}
              data-testid={`exposure-badge-${p.id}`}
            >
              {p.label}
            </span>
          );
        })}
      </div>
      {durationFor && (
        <DurationRow
          custom={customDuration}
          onCustom={setCustomDuration}
          onPick={(rounds) => pick(durationFor, rounds)}
        />
      )}
    </div>
  );
}

interface DurationRowProps {
  custom: string;
  onCustom: (value: string) => void;
  onPick: (rounds: number) => void;
}

function DurationRow({ custom, onCustom, onPick }: DurationRowProps) {
  const pickCustom = () => onPick(Math.max(1, parseInt(custom, 10) || 1));
  return (
    <div className={styles.subPanel} role="group" aria-label="Duration picker">
      <div className={styles.subPanelLabel}>Duration (rounds)</div>
      <div className={styles.presetRow}>
        {DURATION_PRESETS.map((n) => (
          <button key={n} className={styles.presetBtn} onClick={() => onPick(n)} type="button">
            {n}
          </button>
        ))}
        <button
          className={styles.presetBtn}
          onClick={() => onPick(0)}
          type="button"
          title="No countdown - stays until removed"
          data-testid="exposure-permanent"
        >
          Permanent
        </button>
        <input
          className={styles.customInput}
          type="number"
          min={1}
          value={custom}
          placeholder="#"
          onChange={(e) => onCustom(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && pickCustom()}
          aria-label="Custom duration"
        />
        {custom && (
          <button className={styles.customInputConfirm} onClick={pickCustom} type="button">
            Add
          </button>
        )}
      </div>
    </div>
  );
}
